/**
 * Cookie consent storage helpers — single source of truth for all PontiMed
 * apps. Mirrors legacy `src/components/layout/cookie-consent.tsx` 1:1 for
 * GDPR + ePrivacy regulatory parity.
 *
 * Storage uses an actual cookie (not localStorage) so SSR can read consent
 * without hydration mismatch and so the user's choice survives across all
 * PontiMed subdomains under `.pontimed.app` once the cookie is set with a
 * domain attribute (set the domain via the host app's banner save handler).
 *
 * Categories (per Tudor approval 2026-05-01):
 *   - necessary    — always true (auth, CSRF, language preference)
 *   - functional   — remembers preferences (language, country, theme)
 *   - analytics    — PostHog, Google Analytics (default OFF, opt-in)
 *   - marketing    — Facebook Pixel, Google Ads (default OFF, opt-in)
 *
 * Re-prompt cadence: 393 days (CNIL recommendation, ~13 months) or version bump.
 */

export const COOKIE_KEY = "cookie_consent";
// CNIL recommendation: re-prompt for consent every 13 months (393 days).
// See _docs/legal/compliance-framework.md §2.1 (cookie consent v2).
export const COOKIE_MAX_AGE = 393 * 24 * 60 * 60;

/**
 * Bumped any time the consent schema or regulatory wording changes.
 * Mismatched versions trigger a re-prompt automatically.
 */
export const CONSENT_VERSION = "v2.0-2026-04-27";

/** Custom DOM event broadcast on every consent save — analytics SDKs subscribe
 *  to this and (re-)init or tear down based on the new flags. */
export const CONSENT_UPDATE_EVENT = "cookie-consent-update";

export interface CookieConsent {
  necessary: true; // always true (strictly necessary cookies)
  functional: boolean; // remembers preferences (language, country, theme)
  analytics: boolean; // PostHog, GA, Plausible, etc.
  marketing: boolean; // ads + retargeting
  timestamp: string;
  version: string;
}

export function getStoredConsent(): CookieConsent | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = document.cookie
      .split("; ")
      .find((c) => c.startsWith(`${COOKIE_KEY}=`))
      ?.split("=")[1];
    if (!raw) return null;
    const parsed = JSON.parse(decodeURIComponent(raw)) as Partial<CookieConsent>;
    // Re-prompt if consent was given on an older version of the policy.
    if (parsed.version !== CONSENT_VERSION) return null;
    if (typeof parsed.functional !== "boolean") return null;
    if (typeof parsed.analytics !== "boolean") return null;
    if (typeof parsed.marketing !== "boolean") return null;
    return parsed as CookieConsent;
  } catch {
    return null;
  }
}

export interface SetConsentOptions {
  /** Cookie `Domain` attribute. Set to `.pontimed.app` in production so
   *  consent persists across subdomains (web, portal, admin). Omit in dev. */
  domain?: string;
}

export function setConsentCookie(consent: CookieConsent, options: SetConsentOptions = {}) {
  const value = encodeURIComponent(JSON.stringify(consent));
  const domain = options.domain ? `; Domain=${options.domain}` : "";
  document.cookie = `${COOKIE_KEY}=${value}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax${domain}`;
}

/**
 * Dispatch a custom event so analytics scripts can react to consent changes
 * without re-reading the cookie. Listeners should treat the absence of
 * `analytics` / `marketing` as "denied".
 *
 * Also updates Google Analytics consent mode if `gtag` is loaded — no-op when
 * GA isn't on the page (cheap to call unconditionally).
 */
export function dispatchConsentEvent(consent: CookieConsent) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(CONSENT_UPDATE_EVENT, { detail: consent }));

  // Update Google Analytics consent mode if gtag exists (no-op until GA loads).
  if (typeof window.gtag === "function") {
    window.gtag("consent", "update", {
      analytics_storage: consent.analytics ? "granted" : "denied",
      ad_storage: consent.marketing ? "granted" : "denied",
      ad_user_data: consent.marketing ? "granted" : "denied",
      ad_personalization: consent.marketing ? "granted" : "denied",
    });
  }
}

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}
